import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import StaffShell from '../../StaffShell';
import { inputClass, labelClass } from '../../AuthShell';
import staffApi, { apiError } from '../../api';
import AnimatedNumber from '../../components/AnimatedNumber';
import DonutChart from '../../components/DonutChart';
import EmptyState from '../../components/EmptyState';
import { SkeletonTiles, SkeletonRows } from '../../components/Skeleton';

const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);

const STAT_DEFS = [
  { key: 'jobs_assigned', label: 'Jobs assigned', color: '#77CD0C' },
  { key: 'jobs_completed', label: 'Completed', color: '#2f7ce6' },
  { key: 'jobs_pending', label: 'Pending', color: '#f0b429' },
  { key: 'follow_up_required', label: 'Follow-up', color: '#5aa0f2' },
  { key: 'customer_not_available', label: 'Cust. N/A', color: '#e5674f' },
  { key: 'installs_completed', label: 'Installs done', color: '#1466d6' },
  { key: 'repairs_completed', label: 'Repairs done', color: '#ffbb45' },
];

const stagger = { show: { transition: { staggerChildren: 0.05 } } };
const rise = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };

export default function Dashboard() {
  const [date, setDate] = useState(today());
  const [month, setMonth] = useState(thisMonth());
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [techs, setTechs] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoadingSummary(true);
    staffApi.get('/dashboard/summary/', { params: { date } })
      .then((r) => setSummary(r.data))
      .catch((err) => setError(apiError(err)))
      .finally(() => setLoadingSummary(false));
  }, [date]);

  useEffect(() => {
    setLoadingStats(true);
    staffApi.get('/dashboard/technicians/', { params: { month } })
      .then((r) => setTechs(r.data.technicians || []))
      .catch(() => setTechs([]))
      .finally(() => setLoadingStats(false));
  }, [month]);

  return (
    <StaffShell backTo="/staff/app" max="max-w-4xl">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="text-lime text-[11px] font-bold uppercase tracking-[0.25em]">Operations</p>
          <h1 className="text-white text-2xl sm:text-3xl font-extrabold">Dashboard</h1>
        </div>
        <div>
          <label className={`${labelClass} text-white/70`}>Summary date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inputClass} max-w-[12rem]`} />
        </div>
      </div>

      {error && <p className="text-red-300 text-sm mb-4">{error}</p>}

      {/* Stat tiles */}
      {loadingSummary && !summary ? (
        <SkeletonTiles count={7} />
      ) : summary ? (
        <motion.div className="grid grid-cols-2 sm:grid-cols-4 gap-3" variants={stagger} initial="hidden" animate="show">
          {STAT_DEFS.map((s) => (
            <motion.div key={s.key} variants={rise} whileHover={{ y: -4 }}
              className="relative overflow-hidden rounded-2xl border border-line bg-surface p-4">
              <span className="absolute top-3 right-3 h-2.5 w-2.5 rounded-full"
                style={{ background: s.color, boxShadow: `0 0 10px ${s.color}` }} />
              <div className="text-3xl font-extrabold" style={{ color: s.color }}>
                <AnimatedNumber value={summary[s.key] ?? 0} />
              </div>
              <div className="text-ink-soft text-sm mt-1">{s.label}</div>
            </motion.div>
          ))}
        </motion.div>
      ) : null}

      {/* Status breakdown donut */}
      {summary && (
        <motion.div variants={rise} initial="hidden" animate="show"
          className="mt-5 rounded-2xl border border-line bg-surface p-5">
          <h3 className="text-ink font-bold mb-4">Status breakdown · {summary.date}</h3>
          {summary.jobs_assigned === 0 ? (
            <EmptyState emoji="🌙" title="A quiet day" message={`No jobs logged on ${summary.date}.`} />
          ) : (
            <DonutChart data={[
              { label: 'Completed', value: summary.jobs_completed, color: '#2f7ce6' },
              { label: 'Pending', value: summary.jobs_pending, color: '#f0b429' },
              { label: 'Follow-up', value: summary.follow_up_required, color: '#5aa0f2' },
              { label: 'Cust. N/A', value: summary.customer_not_available, color: '#e5674f' },
            ]} />
          )}
        </motion.div>
      )}

      {/* Materials used */}
      {summary && (
        <motion.div variants={rise} initial="hidden" animate="show"
          className="mt-5 rounded-2xl border border-line bg-surface p-5">
          <h3 className="text-ink font-bold mb-3">Materials used · {summary.date}</h3>
          {summary.materials_used?.length ? (
            <ul className="divide-y divide-line">
              {summary.materials_used.map((m) => (
                <li key={m.material} className="flex justify-between py-2 text-ink">
                  <span>{m.material}</span>
                  <span className="font-semibold">{m.total_quantity} <span className="text-ink-soft text-sm">{m.unit}</span></span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState emoji="📦" title="No materials logged" message={`Nothing was consumed on ${summary.date}.`} />
          )}
        </motion.div>
      )}

      {/* Technician performance */}
      <div className="mt-5 rounded-2xl border border-line bg-surface p-5">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-3">
          <h3 className="text-ink font-bold">Technician performance</h3>
          <div>
            <label className={labelClass}>Month</label>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={`${inputClass} max-w-[12rem]`} />
          </div>
        </div>

        {loadingStats && !techs ? (
          <SkeletonRows rows={4} />
        ) : techs && techs.length === 0 ? (
          <EmptyState emoji="🗓️" title="No reports this month" message={`Nothing logged for ${month}.`} />
        ) : (
          <div className="space-y-3">
            {(techs || []).map((t, i) => {
              const rate = t.total ? Math.round((t.completed / t.total) * 100) : 0;
              return (
                <motion.div key={t.technician_id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-ink font-semibold truncate">{t.username}</div>
                    <div className="mt-1 h-1.5 rounded-full bg-surface-2 overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${rate}%` }}
                        transition={{ duration: 0.8, delay: i * 0.05 }}
                        className="h-full rounded-full bg-lime" />
                    </div>
                  </div>
                  <div className="flex gap-3 text-sm font-semibold shrink-0">
                    <span className="text-ink">{t.total}</span>
                    <span className="text-[#2f7ce6]">{t.completed}</span>
                    <span className="text-amber-400">{t.pending}</span>
                    <span className="text-sky-400">{t.follow_up}</span>
                  </div>
                </motion.div>
              );
            })}
            <div className="flex justify-end gap-3 text-[11px] text-ink-soft pt-1">
              <span>Total</span><span className="text-[#2f7ce6]">Done</span><span className="text-amber-400">Pending</span><span className="text-sky-400">Follow-up</span>
            </div>
          </div>
        )}
      </div>
    </StaffShell>
  );
}
